import {
  InventoryUOMMapping,
  ProductDoc,
  ProductInventory,
  ProductInventoryUnit,
  PurchaseBundleType,
} from '@gooduncles/gu-app-schema';
import { Button, Divider, Form, Input, InputNumber, Modal, Select, Tabs, message } from 'antd';
import { FC, useEffect, useState } from 'react';

import { formatNumber } from 'src/utils/common';
import {
  batchEnd,
  batchStart,
  createProductInventory,
  getProductInventoryId,
  updateProduct,
  updateProductInventory,
} from 'src/utils/firebase/firebase-short-cut';
import { getInventoryNameForProduct } from 'src/utils/product-inventory-util';

import { useInventory } from 'src/redux/hooks/useInventory';

import useSWRProductInventories from 'src/hooks/swr/useSWRProductInventories';
import useSWRProductInventory from 'src/hooks/swr/useSWRProductInventory';
import useSWRProductSuppliers from 'src/hooks/swr/useSWRProductSuppliers';

import InventoryPurchaseConfig from '../../../atomic-components/molecules/Inventory/Form/InventoryPurchaseConfig';
import InventoryUOMConfig from '../../../atomic-components/molecules/Inventory/Form/InventoryUOMConfig';
import Label from 'src/atomic-components/atoms/TextItem/Label';

interface EditInventoryForProductModalProps {
  product: ProductDoc;
}

const EditInventoryForProductModal: FC<EditInventoryForProductModalProps> = ({ product }) => {
  const { onSetSelectedProduct } = useInventory();
  const [selectedInventoryUnit, setSelectedInventoryUnit] = useState<ProductInventoryUnit>(ProductInventoryUnit.EA);
  const [inventoryIdForHook, setInventoryIdForHook] = useState<string>(product.inventoryId);
  const [showNewUnitConfig, setShowNewUnitConfig] = useState<boolean>(false);
  const { inventory, refresh: refreshInventory } = useSWRProductInventory(inventoryIdForHook);
  const {
    productInventories,
    isLoading: isLoadingProductInventories,
    refresh: refreshProductInventories,
  } = useSWRProductInventories('all-product-inventories', [['deletedAt', '==', null]]);
  const { productSuppliers, isLoading: isLoadingProductSuppliers } = useSWRProductSuppliers([
    ['deletedAt', '==', null],
  ]);
  const [activeTab, setActiveTab] = useState<'existing' | 'new'>('existing');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newInventoryForm] = Form.useForm();
  const [existingInventoryForm] = Form.useForm();
  const [existingUnitConfigForm] = Form.useForm();

  // activeTab이 변경될 때마다 inventory hook 호출 데이터 업데이트
  useEffect(() => {
    setInventoryIdForHook(product.inventoryId);
  }, [activeTab, product.inventoryId]);

  // 탭 바뀔때 폼 초기화
  useEffect(() => {
    if (activeTab === 'new') {
      newInventoryForm.setFieldsValue({
        inventoryName: getInventoryNameForProduct(product),
        quantity: Infinity,
        inventoryUnit: ProductInventoryUnit.EA,
        eaRatio: 1,
        eaCost: product.cost,
        eaBarcode: '',
        bundleRatio: 5,
        bundleCost: product.cost * 5,
        bundleBarcode: '',
        supplierId: undefined,
        purchaseProductName: product.fullName,
        purchaseBundle: PurchaseBundleType.ALL,
        purchaseMaxBundle: null,
        purchaseMultiplyUnit: 1,
        purchaseUnit: '개',
      });
    }
  }, [activeTab, newInventoryForm, product]);

  // 기존 재고 선택 탭에서 재고 선택 시 폼 초기화
  useEffect(() => {
    if (inventory) {
      const inventoryUnit = Object.keys(inventory.uom)[0] as ProductInventoryUnit;
      existingInventoryForm.setFieldsValue({
        inventoryName: inventory.inventoryName,
        quantity: inventory.quantity,
        inventoryUnit,
      });

      if (showNewUnitConfig) {
        existingUnitConfigForm.setFieldsValue({
          ratio: inventory.uom[inventoryUnit]?.ratio,
          cost: inventory.uom[inventoryUnit]?.cost,
          barcode: inventory.uom[inventoryUnit]?.barcode,
        });
      }
    }
  }, [existingInventoryForm, existingUnitConfigForm, inventory, showNewUnitConfig]);

  const handleInventoryChange = async (inventoryId: string) => {
    try {
      setIsLoading(true);
      batchStart();

      const inventoryUpdates: Partial<ProductInventory> = {};

      // 새로운 단위 설정이 필요한 경우 uom 업데이트
      if (showNewUnitConfig && inventory) {
        const values = await existingUnitConfigForm.validateFields();
        inventoryUpdates.uom = {
          ...inventory.uom,
          [selectedInventoryUnit]: {
            ratio: values.ratio,
            cost: values.cost,
            barcode: values.barcode || null,
          },
        };
      }

      // 재고명 변경 적용
      if (inventory) {
        const formInventoryName = (existingInventoryForm.getFieldValue('inventoryName') ?? '').trim();
        const nextInventoryName = formInventoryName || getInventoryNameForProduct(product);
        if (nextInventoryName !== inventory.inventoryName) {
          inventoryUpdates.inventoryName = nextInventoryName;
        }
      }

      if (Object.keys(inventoryUpdates).length > 0) {
        await updateProductInventory(inventoryId, inventoryUpdates, { bBatch: true });
      }

      await updateProduct(
        product._id,
        {
          inventoryId,
          inventoryUnit: selectedInventoryUnit,
        },
        { bBatch: true },
      );
      await batchEnd();
      refreshInventory();
      refreshProductInventories();
      message.success('재고가 연결되었습니다');
      if (inventoryUpdates.inventoryName) {
        message.warning('재고명이 변경되었습니다. 발주명은 별도로 업데이트해야 합니다. 재고 상세에서 확인해주세요.', 5);
      }
      handleCloseModal();
    } catch (error) {
      console.error(error);
      message.error('재고 연결 중 오류가 발생했습니다');
    } finally {
      setIsLoading(false);
      setShowNewUnitConfig(false);
    }
  };

  const handleCreateInventory = async () => {
    try {
      await newInventoryForm.validateFields();
      const values = newInventoryForm.getFieldsValue();
      console.log(values);
      setIsLoading(true);

      const inventoryId = getProductInventoryId();

      // uom 객체 구성
      const uom: InventoryUOMMapping = {};

      // 선택된 단위 추가
      if (values.inventoryUnit === ProductInventoryUnit.EA) {
        uom[ProductInventoryUnit.EA] = {
          ratio: values.eaRatio,
          cost: values.eaCost,
          barcode: values.eaBarcode || null,
        };
      } else if (values.inventoryUnit === ProductInventoryUnit.BUNDLE) {
        uom[ProductInventoryUnit.BUNDLE] = {
          ratio: values.bundleRatio,
          cost: values.bundleCost,
          barcode: values.bundleBarcode || null,
        };
      }

      // 인벤토리명이 없는 경우 자동 생성
      const inventoryName = values.inventoryName || getInventoryNameForProduct(product);

      // 신규 인벤토리 생성
      const newInventory: ProductInventory = {
        inventoryName,
        quantity: values.quantity,
        uom,
        purchaseConfig: {
          supplierId: values.supplierId,
          purchaseProductName: values.purchaseProductName,
          purchaseBundle: values.purchaseBundle,
          purchaseMaxBundle: values.purchaseMaxBundle,
          purchaseMultiplyUnit: values.purchaseMultiplyUnit,
          purchaseUnit: values.purchaseUnit,
        },
        deletedAt: null,
        active: true,
        optimalStockMemo: null,
      };

      batchStart();
      await createProductInventory(inventoryId, newInventory, {
        bBatch: true,
      });

      // 상품에 인벤토리 ID 연결 - 선택된 단위로 설정
      await updateProduct(
        product._id,
        {
          inventoryId,
          inventoryUnit: values.inventoryUnit,
        },
        { bBatch: true },
      );
      await batchEnd();

      message.success('새 재고가 생성되었습니다');
      refreshInventory();
      refreshProductInventories();
      handleCloseModal();
    } catch (error) {
      console.error(error);
      message.error('재고 생성 중 오류가 발생했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyExistingInventory = () => {
    if (inventory) {
      handleInventoryChange(inventory._id);
    } else {
      message.warning('재고를 선택해주세요');
    }
  };

  const handleSelectInventory = (inventoryId: string) => {
    // 선택한 재고에서 사용 가능한 단위 타입 체크
    setInventoryIdForHook(inventoryId);
  };

  const handleSelectInventoryUnit = (value: ProductInventoryUnit) => {
    setSelectedInventoryUnit(value);
    // 인벤토리에 해당 단위가 없는 경우 설정 UI 표시
    if (inventory && !inventory.uom[value]) {
      setShowNewUnitConfig(true);
    } else {
      setShowNewUnitConfig(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedInventoryUnit(ProductInventoryUnit.EA);
    onSetSelectedProduct(null);
  };

  return (
    <Modal
      title='재고 관리'
      open={!!product}
      centered
      onCancel={handleCloseModal}
      onOk={activeTab === 'new' ? handleCreateInventory : handleApplyExistingInventory}
      confirmLoading={isLoading}
      okText={activeTab === 'existing' ? '적용' : '생성'}
      cancelText='닫기'
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{
        disabled: isLoading || (activeTab === 'existing' && !inventory),
      }}
      width={720}
      styles={{
        body: { maxHeight: '80vh', overflow: 'auto', paddingRight: 10 },
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'existing' | 'new')}
        items={[
          {
            key: 'existing',
            label: '기존 재고 선택',
            children: (
              <div style={{ padding: '16px 0' }}>
                <Form form={existingInventoryForm} layout='vertical'>
                  <Form.Item label='재고 선택'>
                    <Select
                      showSearch
                      style={{ width: '100%' }}
                      placeholder='재고 선택'
                      loading={isLoadingProductInventories}
                      disabled={isLoadingProductInventories}
                      value={inventoryIdForHook}
                      options={productInventories?.map((item) => ({
                        label: item.inventoryName,
                        value: item._id,
                      }))}
                      onChange={handleSelectInventory}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  {inventory && (
                    <>
                      <Divider />
                      <h3 className='sectionTitle'>선택한 재고 정보</h3>
                      <Form.Item name='inventoryName' label='재고명' help='비워두면 상품명에서 자동생성'>
                        <Input />
                      </Form.Item>
                      <Label style={{ color: 'var(--gray900)' }}>{`재고 수량: ${formatNumber(
                        inventory.quantity,
                      )}`}</Label>

                      <Divider />
                      <h3 className='sectionTitle'>연결 설정</h3>
                      <Form.Item
                        label='사용 단위'
                        name='inventoryUnit'
                        required
                        tooltip='상품이 어떤 단위로 재고를 사용할지 설정합니다'
                      >
                        <Select
                          value={selectedInventoryUnit}
                          onChange={handleSelectInventoryUnit}
                          options={[
                            { label: '개별 단위 (EA)', value: ProductInventoryUnit.EA },
                            { label: '묶음 단위 (Bundle)', value: ProductInventoryUnit.BUNDLE },
                          ]}
                        />
                      </Form.Item>

                      {showNewUnitConfig && (
                        <div
                          style={{
                            backgroundColor: 'var(--gray100)',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                          }}
                        >
                          <h4 style={{ marginBottom: '16px' }}>새 단위 설정</h4>
                          <Form form={existingUnitConfigForm} layout='vertical'>
                            <Form.Item name='ratio' label='비율' required>
                              <InputNumber style={{ width: '100%' }} min={1} />
                            </Form.Item>
                            <Form.Item name='cost' label='원가' required>
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                formatter={(value) => formatNumber(value ?? 0)}
                              />
                            </Form.Item>
                            <Form.Item name='barcode' label='바코드'>
                              <Input />
                            </Form.Item>
                          </Form>
                        </div>
                      )}
                    </>
                  )}
                </Form>
              </div>
            ),
          },
          {
            key: 'new',
            label: '새 재고 생성',
            children: (
              <div style={{ padding: '16px 0' }}>
                <Form form={newInventoryForm} layout='vertical'>
                  <h3 className='sectionTitle'>기본 정보</h3>
                  <Form.Item name='inventoryName' label='재고명' help='비워두면 상품명에서 자동생성'>
                    <Input />
                  </Form.Item>

                  <Form.Item name='quantity' label='재고 수량' tooltip='Infinity는 재고 관리를 하지 않는 상품입니다'>
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      addonAfter={
                        <Button
                          size='small'
                          type='text'
                          onClick={() => newInventoryForm.setFieldsValue({ quantity: Infinity })}
                          style={{ padding: '0 8px' }}
                        >
                          무제한
                        </Button>
                      }
                    />
                  </Form.Item>

                  <Divider />
                  <InventoryUOMConfig
                    product={product}
                    initialValues={{
                      inventoryUnit: ProductInventoryUnit.EA,
                      uom: {
                        [ProductInventoryUnit.EA]: {
                          ratio: 1,
                          cost: product.cost,
                          barcode: '',
                        },
                        [ProductInventoryUnit.BUNDLE]: {
                          ratio: 5,
                          cost: product.cost * 5,
                          barcode: '',
                        },
                      },
                    }}
                  />

                  <Divider />
                  <InventoryPurchaseConfig
                    product={product}
                    productSuppliers={productSuppliers}
                    isLoadingProductSuppliers={isLoadingProductSuppliers}
                    initialValues={{
                      supplierId: undefined,
                      purchaseProductName: product.fullName,
                      purchaseBundle: PurchaseBundleType.ALL,
                      purchaseMaxBundle: null,
                      purchaseMultiplyUnit: 1,
                      purchaseUnit: '개',
                    }}
                  />
                </Form>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default EditInventoryForProductModal;
